import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArchive, FiPlus, FiTrash2, FiFolder, FiClock, FiImage, FiHardDrive, FiInfo } from 'react-icons/fi';
import { apiFetch } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { getFeatureFlags } from '../utils/featureFlags';
import { showToast } from './NotificationTray';
import PageHero from './PageHero';
import PageHelp from './PageHelp';

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const days = Math.floor(diffMs / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return '1 month ago';
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(months / 12);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

export default function Vault() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const features = getFeatureFlags(user);

  const fetchData = useCallback(async () => {
    try {
      const [colRes, usageRes] = await Promise.all([
        apiFetch('/api/vault/collections'),
        apiFetch('/api/vault/usage'),
      ]);

      if (colRes.ok) {
        const data = await colRes.json();
        setCollections(data.collections || []);
      }
      if (usageRes.ok) {
        const data = await usageRes.json();
        setUsage(data);
      }
    } catch (err) {
      console.error('Failed to load vault:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await apiFetch('/api/vault/collections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), description: newDesc.trim() || undefined }),
      });
      if (res.ok) {
        const data = await res.json();
        showToast('Collection created', 'success');
        setShowCreate(false);
        setNewName('');
        setNewDesc('');
        navigate(`/vault/${data.collection.id}`);
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to create', 'error');
      }
    } catch (err) {
      showToast('Failed to create collection', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, id, name) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(`Delete "${name}"? This will permanently remove all files in this collection.`)) return;
    try {
      const res = await apiFetch(`/api/vault/collections/${id}`, { method: 'DELETE' });
      if (res.ok) {
        showToast('Collection deleted', 'success');
        setCollections(prev => prev.filter(c => c.id !== id));
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to delete', 'error');
      }
    } catch (err) {
      showToast('Failed to delete collection', 'error');
    }
  };

  // Plan gating
  if (!features.vault) {
    return (
      <>
        <PageHero eyebrow="Cold storage" title="Photo Vault" />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <FiArchive size={48} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Unlimited Cold Storage</h2>
          <p className="text-gray-500 mb-6">
            Archive old shoots, client deliveries, and personal history. Browse anytime with instant previews.
            Only pay for what you store at $24/TB/month. Available on Pro plans and above.
          </p>
          <Link
            to="/settings"
            className="inline-block px-6 py-2.5 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
          >
            Upgrade your plan
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Cold storage"
        title="Photo Vault"
        stats={usage ? [
          { label: 'Files', value: usage.total_files.toLocaleString() },
          { label: 'Archived', value: formatBytes(usage.total_bytes) },
          { label: 'Est. monthly', value: `$${usage.estimated_monthly_cost.toFixed(2)}` },
        ] : []}
      >
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
        >
          <FiPlus size={16} />
          New Collection
        </button>
      </PageHero>

      <div className="page-toolbar">
        <PageHelp
          title="About Photo Vault"
          description="Photo Vault is a separate, usage-based archive service. It is not connected to your plan's storage, you only pay for what you store."
          tips={[
            'Pricing: $0.024/GB per month ($24/TB). No minimums, no caps.',
            'Previews: JPG thumbnails are generated automatically for every file so you can browse your archive visually.',
            'RAW support: CR2, NEF, ARW, DNG, HEIC, and more, previews generated from RAW originals.',
            'Retrieval: Request your originals anytime. Download link delivered within 24-72 hours, valid for 72 hours.',
            'Billing: Metered daily, billed monthly. Completely separate from your plan subscription.',
          ]}
          subject="Photo Harbor Help - Photo Vault"
        />
      </div>

      <div className="mobile-content-wrapper" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        {/* Vault explainer */}
        <div style={{
          background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px',
          padding: '1rem 1.25rem', marginBottom: '1.5rem', fontSize: '0.8125rem'
        }}>
          <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '0.75rem', fontSize: '0.875rem' }}>
            <FiArchive size={14} style={{ verticalAlign: '-2px', marginRight: '0.375rem' }} />
            Photo Vault, Separate from your subscription
          </div>
          <div style={{ color: '#35494a', lineHeight: 1.6, display: 'grid', gap: '0.375rem' }}>
            <div><strong style={{ color: '#92400e' }}>$0.024/GB</strong> per month ($24/TB). No minimums, no caps, pay only for what you store.</div>
            <div>Designed for long-term archive: old shoots, client deliveries, personal history.</div>
            <div>Browse anytime with auto-generated previews. Retrieve originals on request (24-72h).</div>
            <div style={{ marginTop: '0.25rem', fontWeight: 600, color: '#92400e', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              Billed separately from your plan. Does not count against your plan quota.
            </div>
          </div>
        </div>

        {/* Create modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowCreate(false)}>
            <form
              onClick={e => e.stopPropagation()}
              onSubmit={handleCreate}
              className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
            >
              <h2 className="text-lg font-semibold mb-4">New Collection</h2>
              <label className="block mb-3">
                <span className="text-sm font-medium text-gray-700">Name</span>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Wedding Archive 2024"
                  autoFocus
                />
              </label>
              <label className="block mb-4">
                <span className="text-sm font-medium text-gray-700">Description (optional)</span>
                <textarea
                  value={newDesc}
                  onChange={e => setNewDesc(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Add a note about this collection"
                />
              </label>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newName.trim()}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Loading state */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {[1,2,3].map(i => <div key={i} className="h-64 bg-gray-100 rounded-lg animate-pulse" />)}
          </div>
        ) : collections.length === 0 ? (
          /* Empty state */
          <div className="text-center py-16">
            <FiArchive size={48} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">Your Vault is empty</h3>
            <p className="text-gray-400 mb-6" style={{ maxWidth: '400px', margin: '0 auto 1.5rem' }}>
              Upload old projects you want to preserve but don't need daily access to.
              You only pay for what you store.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600"
            >
              <FiPlus size={16} />
              Create your first collection
            </button>
          </div>
        ) : (
          /* Collection grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {collections.map(col => {
              const proxyCount = col.total_files > 0
                ? Math.round((col.proxy_bytes > 0 ? 1 : 0) * col.total_files) // approximate
                : 0;

              return (
                <Link
                  key={col.id}
                  to={`/vault/${col.id}`}
                  className="group border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white"
                >
                  {/* Cover image */}
                  <div className="h-40 bg-gray-100 flex items-center justify-center relative">
                    {col.cover_proxy_url ? (
                      <img src={col.cover_proxy_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <FiFolder size={32} className="text-gray-300" />
                    )}
                    {/* Status badge overlay */}
                    <span className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full font-medium ${
                      col.status === 'archived' ? 'bg-green-100 text-green-700'
                      : col.status === 'active' ? 'bg-blue-100 text-blue-700'
                      : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {col.status === 'archived' ? 'Archived' : col.status === 'active' ? 'Active' : col.status}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800 truncate mb-2">{col.name}</h3>
                    {col.description && (
                      <p className="text-xs text-gray-400 truncate mb-3">{col.description}</p>
                    )}

                    {/* Stats grid */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <FiImage size={12} className="text-gray-400 flex-shrink-0" />
                        <span><strong className="text-gray-700">{col.total_files}</strong> file{col.total_files !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <FiHardDrive size={12} className="text-gray-400 flex-shrink-0" />
                        <span><strong className="text-gray-700">{formatBytes(col.total_bytes)}</strong></span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <FiClock size={12} className="text-gray-400 flex-shrink-0" />
                        <span>{col.archived_at ? `Archived ${timeAgo(col.archived_at)}` : `Created ${timeAgo(col.created_at)}`}</span>
                      </div>
                      {col.proxy_bytes > 0 && (
                        <div className="flex items-center gap-1.5 text-gray-500">
                          <FiFolder size={12} className="text-gray-400 flex-shrink-0" />
                          <span>Previews: {formatBytes(col.proxy_bytes)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer with delete */}
                  <div className="px-4 pb-3 flex items-center justify-between border-t border-gray-100 pt-2">
                    <span className="text-xs text-gray-400">
                      {new Date(col.created_at).toLocaleDateString()}
                    </span>
                    <button
                      onClick={(e) => handleDelete(e, col.id, col.name)}
                      className="text-xs text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
                    >
                      <FiTrash2 size={12} />
                      Delete
                    </button>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
